import { Dispatch } from 'redux';
import { Services } from '../../types/services';

const COMMUNICATIONS_ERROR = 'COMMUNICATIONS_ERROR';
const GET_COMMENTS = 'GET_COMMENTS';
const GET_COMMUNICATION = 'GET_COMMUNICATION';
const GET_COMMUNICATION_LIST = 'GET_COMMUNICATION_LIST';
const SET_COMMUNICATION_ACTIVITY = 'SET_COMMUNICATION_ACTIVITY';
const SET_SELECTED_COMMUNICATION = 'SET_SELECTED_COMMUNICATION';

interface Comment {
  id: string;
  text: string;
  [key: string]: unknown;
}

interface Communication {
  id?: string;
  comments?: Comment[];
  [key: string]: unknown;
}

interface CommunicationActivity {
  [key: string]: unknown;
}

interface State {
  allCommunications: Communication[];
  communicationActivity: CommunicationActivity[];
  communication: Communication[];
  comments: Comment[];
  selectedCommunication: Communication;
  loading: boolean;
  error: string;
}

const initialState: State = {
  allCommunications: [],
  communicationActivity: [],
  communication: [],
  comments: [],
  selectedCommunication: {},
  loading: true,
  error: '',
};

interface Action {
  type: string;
  payload?: unknown;
}

export const reducer = (state = initialState, action: Action): State => {
  switch (action.type) {
    case GET_COMMUNICATION_LIST:
      return {
        ...state,
        allCommunications: action.payload as Communication[],
      };
    case COMMUNICATIONS_ERROR:
      return {
        ...state,
        communication: [],
        loading: false,
        error: action.payload as string,
      };
    case GET_COMMUNICATION:
      return {
        ...state,
        communication: action.payload as Communication[],
      };
    case GET_COMMENTS:
      return {
        ...state,
        comments: action.payload as Comment[],
      };
    case SET_SELECTED_COMMUNICATION:
      return {
        ...state,
        selectedCommunication: action.payload as Communication,
      };
    case SET_COMMUNICATION_ACTIVITY:
      return {
        ...state,
        communicationActivity: action.payload as CommunicationActivity[],
      };
    default:
      return state;
  }
};

export const actions = (services: Services) => {
  const axiosService = services.axios.Axios();

  const communicationsError = (error: string) => ({
    type: COMMUNICATIONS_ERROR,
    payload: error,
  });

  const fetchComments = (communicationId: string) => {
    return async (dispatch: Dispatch) => {
      try {
        const response = await axiosService.get<{ data: Communication }>(
          `Communications/${communicationId}`,
        );
        const comments = response.data.data;
        dispatch({ type: SET_SELECTED_COMMUNICATION, payload: comments });
        dispatch({ type: GET_COMMENTS, payload: comments?.comments || [] });
      } catch (error: unknown) {
        const errorMsg = error instanceof Error ? error.message : 'An unknown error occurred';
        dispatch(communicationsError(errorMsg));
      }
    };
  };

  const readCommunicationComments = (communicationId: string) => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosService.post(`communications/${communicationId}/read`);
      } catch (error: unknown) {
        const errorMsg = error instanceof Error ? error.message : 'An unknown error occurred';
        dispatch(communicationsError(errorMsg));
      }
    };
  };

  const fetchCommunicationsList = (
    practiceID: string,
    includeClosedCommunications = false,
    _selectedCommunication: Communication | null = null,
  ) => {
    return async (dispatch: Dispatch) => {
      try {
        let communications: Communication[] = [];
        if (practiceID) {
          const response = await axiosService.get<{ data: Communication[] }>(
            `practices/${practiceID}/communications?includeClosedCommunications=${includeClosedCommunications}`,
          );
          communications = response.data.data;
        }
        return communications;
      } catch (error: unknown) {
        const errorMsg = error instanceof Error ? error.message : 'An unknown error occurred';
        dispatch(communicationsError(errorMsg));
      }
    };
  };

  const getCommunications =
    (practiceId: string, includeClosedCommunications = false) =>
    async () => {
      const params: { practiceId: string; includeClosedCommunications?: boolean } = {
        practiceId: practiceId,
      };
      if (includeClosedCommunications === true) params.includeClosedCommunications = true;
      return axiosService.get('communications', { params });
    };

  const createNewCommunication = (payload: FormData) => async () =>
    axiosService.post('communications', payload, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

  const getCommunication = (communicationId: string) => async () =>
    axiosService.get('communications/' + communicationId);

  const postCommunicationComment = (communicationId: string, payload: FormData) => async () =>
    axiosService.post(`communications/${communicationId}/comment`, payload, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

  const getCommunicationUsers = (communicationId: string) => async () =>
    axiosService.get('communications/' + communicationId + '/users');

  const getCommunicationPartners = (practiceId: string) => async () =>
    axiosService.get('communications/' + practiceId + '/partners');

  const markCommentAsUnread = (commentId: string) => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosService.post(`communications/comment/${commentId}/mark`);
      } catch (error: unknown) {
        const errorMsg = error instanceof Error ? error.message : 'An unknown error occurred';
        dispatch(communicationsError(errorMsg));
      }
    };
  };

  const fetchCommunicationActivity = (sortByNewFirst = false) => {
    return async (dispatch: Dispatch) => {
      try {
        const url = sortByNewFirst
          ? `communications/activity?sortByNewFirst=true`
          : `communications/activity`;
        const response = await axiosService.get<{ data: CommunicationActivity[] }>(url);
        dispatch({ type: SET_COMMUNICATION_ACTIVITY, payload: response.data.data });
      } catch (error: unknown) {
        const errorMsg = error instanceof Error ? error.message : 'An unknown error occurred';
        dispatch(communicationsError(errorMsg));
      }
    };
  };

  return {
    createNewCommunication,
    fetchComments,
    fetchCommunicationActivity,
    fetchCommunicationsList,
    getCommunication,
    getCommunicationPartners,
    getCommunicationUsers,
    getCommunications,
    markCommentAsUnread,
    postCommunicationComment,
    readCommunicationComments,
  };
};
